import { defineStore } from 'pinia'
import { ref } from 'vue'
import { createChatCore } from './chatCore.js'
import { useProjectStore } from './projects.js'

// Gli stessi formati del motore backend delle verifiche: il Backend li apre e ne estrae il testo.
const SUPPORTED = /\.(pdf|png|jpe?g|gif|webp|txt|md|log|csv|xml|json|html?|docx?|xlsx?|pptx?|msg|eml|rtf|odt|ods|zip)$/i
const MAX_FILE_BYTES = 20 * 1024 * 1024
const MAX_TOTAL_BYTES = 60 * 1024 * 1024

const fingerprintOf = (info) =>
  (info?.modifiedAt == null || info?.sizeBytes == null)
    ? null
    : `${info.relativePath || info.name}|${info.sizeBytes}|${info.modifiedAt}`

/**
 * Chat su UN processo: il modello vede i campi del processo e i documenti della sua cartella.
 *
 * I documenti li legge il browser dal LocalBackend (il Backend non vede la cartella) e li manda UNA
 * volta per conversazione: il Backend li apre e li tiene in cache. Se la cache scade risponde
 * `DOCS_MISSING` e qui si rimandano, una volta sola.
 *
 * È una chat: non scrive niente nel processo.
 */
export const useProjectChatStore = defineStore('projectChat', () => {
  const projectId = ref(null)
  const preparing = ref(false)
  const documentCount = ref(0)
  const skipped = ref([])
  // Conversazione i cui documenti sono già nella cache del Backend.
  const docsSentFor = ref(null)
  // Acceso da "Aggiorna i documenti": la prossima domanda rilegge la cartella.
  const refreshNext = ref(false)

  const core = createChatCore({
    modelsUrl: () => `/api/projects/${projectId.value}/chat/models`,
    streamUrl: () => `/api/projects/${projectId.value}/chat/stream`,
    buildBody: async ({ history, conversationId, model, retry }) => {
      const body = { conversationId, model, messages: history }
      // `attachments: null` = "usa quelli in cache"; la lista (anche vuota) li ricarica.
      if (retry || refreshNext.value || docsSentFor.value !== conversationId) {
        body.attachments = await collectDocuments()
        docsSentFor.value = conversationId
        refreshNext.value = false
      }
      return body
    },
    retryOn: (message) => String(message).includes('DOCS_MISSING')
  })

  /** Legge la cartella del processo e prepara gli allegati per il Backend. */
  async function collectDocuments() {
    const projectStore = useProjectStore()
    const attachments = []
    skipped.value = []
    preparing.value = true
    try {
      const files = await projectStore.listProjectFiles(projectId.value)
      let total = 0
      for (const info of files) {
        if (!SUPPORTED.test(info.name)) { skipped.value.push(info.name); continue }
        if ((info.sizeBytes || 0) > MAX_FILE_BYTES) { skipped.value.push(info.name); continue }
        if (total + (info.sizeBytes || 0) > MAX_TOTAL_BYTES) { skipped.value.push(info.name); continue }

        const relativePath = info.relativePath || info.name
        const file = await projectStore.readProjectFile(projectId.value, relativePath)
        attachments.push({
          name: file.name,
          mimeType: file.mimeType,
          base64: file.base64,
          path: relativePath,
          fingerprint: fingerprintOf(info)
        })
        total += info.sizeBytes || 0
      }
      documentCount.value = attachments.length
      return attachments
    } finally {
      preparing.value = false
    }
  }

  /** Il pannello si apre su un processo: cambiando processo la conversazione riparte. */
  function setProject(id) {
    if (projectId.value === id) return
    projectId.value = id
    newChat()
  }

  function newChat() {
    core.newChat()
    docsSentFor.value = null
    documentCount.value = 0
    skipped.value = []
    refreshNext.value = false
  }

  /** La prossima domanda rilegge i documenti della cartella (file aggiunti o cambiati). */
  function refreshData() {
    refreshNext.value = true
  }

  return {
    ...core, projectId, preparing, documentCount, skipped, refreshNext,
    setProject, newChat, refreshData
  }
})
